import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ExcavationPageDto, ExcavationLicenseDto, ExcavatorGroupDto } from 'src/app/models/excavation-page-dto';
import { TranslateLangPipe } from 'src/app/components/shared/pipes/translateLang.pipe';
import { RoutingService } from 'src/app/services/routing.service';
import { SearchFieldsService } from 'src/app/services/search-fields.service';
import { FieldsListsService } from 'src/app/services/fields-lists.service';
import { EntityType } from 'src/app/enums/entity-type.enum';
import { RequestKeys } from 'src/app/enums/request-keys.enum';
import { MultiLangStrDto } from 'src/app/models/multi-lang-str-dto';
import { ImageDto } from 'src/app/models/image-dto';

@Component({
  selector: 'excavation-details',
  templateUrl: './excavation-details.component.html',
  styleUrls: ['./excavation-details.component.scss']
})
export class ExcavationDetailsComponent implements OnInit {
  @Input() selectedExcavation!: ExcavationPageDto;
  @Input() currentLanguage!: Observable<string>;
  @Input() selectedEntityId: number | null = null;
  @Output() highlightShape = new EventEmitter<{entityId: number, entityType: string}>();
  
  currentLang: string = 'he';
  expandedLicenses: Set<number> = new Set();
  licensesSectionOpen: boolean = true;
  publicationsSectionOpen: boolean = true;
  filesSectionOpen: boolean = true;
  modelsSectionOpen: boolean = true;
  yazamReportsSectionOpen: boolean = true;
  isUnauthorizedDialogOpen: boolean = false;
  processedUnauthorizedLicenses: Array<{ license: string; expedition: string; }> = [];
  unauthorizedLicenseNumbers: Set<string> = new Set();
  excavationImages: ImageDto[] = [];
  excavationImageThumbnails: ImageDto[] = [];

  constructor(
    private translateLang: TranslateLangPipe,
    private routingService: RoutingService,
    private searchFieldsService: SearchFieldsService,
    private fieldsListsService: FieldsListsService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.currentLanguage.subscribe(lang => {
      this.currentLang = lang;
    });
    
    // Process unauthorized licenses
    this.processUnauthorizedLicenses();
    
    // Process images for gallery
    this.processImages();
    
    // Debug: Log license data
    console.log('Excavation licenses:', this.selectedExcavation?.licenses);
    this.selectedExcavation?.licenses?.forEach((license, i) => {
      console.log(`License ${i}:`, license);
      console.log(`License ${i} excavatorGroups:`, license.excavatorGroups);
    });
    
    // Auto-expand single license
    if(this.selectedExcavation?.licenses?.length === 1) {
      this.expandedLicenses.add(0);
    }
  }

  toggleLicense(index: number): void {
    if (this.expandedLicenses.has(index)) {
      this.expandedLicenses.delete(index);
    } else {
      this.expandedLicenses.add(index);
    }
  }

  isLicenseExpanded(index: number): boolean {
    return this.expandedLicenses.has(index);
  }

  toggleLicensesSection(): void {
    this.licensesSectionOpen = !this.licensesSectionOpen;
  }

  togglePublicationsSection(): void {
    this.publicationsSectionOpen = !this.publicationsSectionOpen;
  }

  toggleFilesSection(): void {
    this.filesSectionOpen = !this.filesSectionOpen;
  }

  toggleModelsSection(): void {
    this.modelsSectionOpen = !this.modelsSectionOpen;
  }

  toggleYazamReportsSection(): void {
    this.yazamReportsSectionOpen = !this.yazamReportsSectionOpen;
  }

  getTranslatedValue(value: any): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value && typeof value === 'object') {
      return this.currentLang === 'he' ? (value.hebrew || value.english || '') : (value.english || value.hebrew || '');
    }
    return '';
  }

  getExcavationName(): string {
    return this.currentLang === 'he' 
      ? (this.selectedExcavation?.excavationNameHebrew || this.selectedExcavation?.excavationNameEnglish || '')
      : (this.selectedExcavation?.excavationNameEnglish || this.selectedExcavation?.excavationNameHebrew || '');
  }

  getExcavatorName(excavatorGroup: ExcavatorGroupDto): string {
    return this.currentLang === 'he'
      ? (excavatorGroup.institutionHebrew || excavatorGroup.institutionEnglish)
      : (excavatorGroup.institutionEnglish || excavatorGroup.institutionHebrew);
  }

  getPersonName(excavator: any): string {
    return this.currentLang === 'he'
      ? (excavator.nameHebrew || excavator.nameEnglish)
      : (excavator.nameEnglish || excavator.nameHebrew);
  }

  navigateToEntity(entityId: number, entityType: string): void {
    this.routingService.navigateToEntity(entityId, entityType);
  }

  navigateToModel(model: any): void {
    if (model?.entityId) {
      this.navigateToEntity(model.entityId, EntityType.PHOTOGRAMMETRIC);
    }
  }

  goTo(personEntityId: number): void {
    // Close entity page and navigate to search results with only persons filter
    // Use navigate without merge to replace all params
    this.routingService['router'].navigate([], { 
      queryParams: {
        persons: personEntityId,
        tabId: 0
      }
    });
  }

  handleExcavatorClick(excavator: any): void {
    if (excavator.personEntityId) {
      // If we have an entity ID, use goTo to trigger search
      this.goTo(excavator.personEntityId);
    } else {
      // Look up the person by name in the persons list
      const searchName = this.getPersonName(excavator);
      if (searchName) {
        // Get the current persons list
        this.fieldsListsService.fieldsLists$.subscribe(lists => {
          const persons = lists.persons || [];
          
          // Try to find the person by name (check both Hebrew and English names)
          const matchingPerson = persons.find(p => 
            p.name?.hebrew === searchName || 
            p.name?.english === searchName ||
            p.name?.hebrew?.includes(searchName) ||
            p.name?.english?.includes(searchName)
          );
          
          if (matchingPerson) {
            // Found the person - use goTo to trigger search
            this.goTo(matchingPerson.entityPersonId);
          } else {
            // Person not found in list - fall back to free text search
            this.routingService['navigateWithMerge']({ 
              entityType: undefined,
              entityId: undefined,
              text: searchName
            });
          }
        }).unsubscribe();
      }
    }
  }

  handleResponsiblePersonClick(): void {
    const responsiblePerson = {
      personEntityId: undefined, // Don't use raw pers_id, let it lookup by name
      nameHebrew: this.selectedExcavation.responsibleForObligationsHeb,
      nameEnglish: this.selectedExcavation.responsibleForObligationsEng
    };
    this.handleExcavatorClick(responsiblePerson);
  }

  openUrl(url: string): void {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  onMapIconClick(entityId: number, entityType: string, event: Event): void {
    event.stopPropagation();
    this.highlightShape.emit({ entityId, entityType });
  }

  hasUnauthorizedContent(): boolean {
    return this.selectedExcavation?.unauthorizedLicenses?.length > 0;
  }

  getUnauthorizedCount(): number {
    return this.selectedExcavation?.unauthorizedLicenses?.length || 0;
  }

  processImages(): void {
    if (this.selectedExcavation?.images) {
      this.excavationImages = this.selectedExcavation.images.map(img => {
        // Build description with model name and date if available
        let description = this.getTranslatedValue(img.modelName);
        if (img.modelDate) {
          description += ` (${img.modelDate})`;
        }
        
        return new ImageDto(
          img.entityId.toString(),
          img.imageUrl,
          img.licenseNumber,
          '', // imageAngle not applicable for models
          description,
          '' // photographerName not applicable for models
        );
      });
      
      this.excavationImageThumbnails = this.excavationImages.map(img => ({
        ...img,
        imageUrl: img.imageUrl.includes('?') 
          ? img.imageUrl + '&type=thumbnail'
          : img.imageUrl + '?type=thumbnail'
      }));
    }
  }

  handleOpenDialog(): void {
    this.isUnauthorizedDialogOpen = true;
  }

  handleCloseDialog(): void {
    this.isUnauthorizedDialogOpen = false;
  }

  processUnauthorizedLicenses(): void {
    if (this.selectedExcavation?.unauthorizedLicenses) {
      this.processedUnauthorizedLicenses = this.selectedExcavation.unauthorizedLicenses.map((entity) => ({
        license: entity.license.longName,
        expedition: entity.expedition.expeditionName.hebrew,
      }));
      // Store license numbers for quick lookup
      this.unauthorizedLicenseNumbers = new Set(
        this.selectedExcavation.unauthorizedLicenses.map(ul => ul.license.longName)
      );
    }
  }

  hasUnauthorizedContentForType(entityType: 'licenses' | 'publications' | 'files' | 'models' | 'reports'): boolean {
    if (!this.selectedExcavation || this.unauthorizedLicenseNumbers.size === 0) {
      return false;
    }

    let items: any[] = [];
    switch (entityType) {
      case 'licenses':
        items = this.selectedExcavation.licenses || [];
        return items.some(item => this.unauthorizedLicenseNumbers.has(item.licenseLongName));
      case 'publications':
        items = this.selectedExcavation.publications || [];
        break;
      case 'files':
        items = this.selectedExcavation.excavationFiles || [];
        break;
      case 'models':
        items = this.selectedExcavation.models || [];
        break;
      case 'reports':
        items = this.selectedExcavation.yazamReports || [];
        break;
    }

    return items.some(item => this.unauthorizedLicenseNumbers.has(item.licenseNumber));
  }

  getUnauthorizedCountForType(entityType: 'licenses' | 'publications' | 'files' | 'models' | 'reports'): number {
    if (!this.selectedExcavation || this.unauthorizedLicenseNumbers.size === 0) {
      return 0;
    }

    let items: any[] = [];
    switch (entityType) {
      case 'licenses':
        items = this.selectedExcavation.licenses || [];
        const matchingLicenses = items.filter(item => this.unauthorizedLicenseNumbers.has(item.licenseLongName));
        return matchingLicenses.length;
      case 'publications':
        items = this.selectedExcavation.publications || [];
        break;
      case 'files':
        items = this.selectedExcavation.excavationFiles || [];
        break;
      case 'models':
        items = this.selectedExcavation.models || [];
        break;
      case 'reports':
        items = this.selectedExcavation.yazamReports || [];
        break;
    }

    const matchingItems = items.filter(item => this.unauthorizedLicenseNumbers.has(item.licenseNumber));
    return matchingItems.length;
  }

  getProcessedUnauthorizedLicensesForType(entityType: 'licenses' | 'publications' | 'files' | 'models' | 'reports'): Array<{ license: string; expedition: string; }> {
    if (!this.selectedExcavation || !this.selectedExcavation.unauthorizedLicenses) {
      return [];
    }

    // Get the license numbers for this entity type
    let items: any[] = [];
    let licenseNumbers: Set<string> = new Set();

    switch (entityType) {
      case 'licenses':
        items = this.selectedExcavation.licenses || [];
        items.forEach(item => licenseNumbers.add(item.licenseLongName));
        break;
      case 'publications':
        items = this.selectedExcavation.publications || [];
        items.forEach(item => licenseNumbers.add(item.licenseNumber));
        break;
      case 'files':
        items = this.selectedExcavation.excavationFiles || [];
        items.forEach(item => licenseNumbers.add(item.licenseNumber));
        break;
      case 'models':
        items = this.selectedExcavation.models || [];
        items.forEach(item => licenseNumbers.add(item.licenseNumber));
        break;
      case 'reports':
        items = this.selectedExcavation.yazamReports || [];
        items.forEach(item => licenseNumbers.add(item.licenseNumber));
        break;
    }

    // Filter unauthorized licenses that match these license numbers
    return this.selectedExcavation.unauthorizedLicenses
      .filter(ul => licenseNumbers.has(ul.license.longName))
      .map(ul => ({
        license: ul.license.longName,
        expedition: ul.expedition.expeditionName.hebrew,
      }));
  }
}
